import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LinkButton,
  LoadingState,
} from '@/components/ui';
import { AccountActions } from '@/features/admin/AccountActions';
import { SignInAsAction } from '@/features/admin/SignInAsAction';
import { VerifyEmailAction } from '@/features/admin/VerifyEmailAction';
import { Fact, FactList } from '@/features/admin/Facts';
import { RecordHeader } from '@/features/admin/RecordHeader';
import {
  ACCOUNT_TONES,
  MEMBER_TONES,
  VERIFICATION_TONES,
  humanizeStatus,
  roleLabel,
} from '@/features/admin/status';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, relativeTime } from '@/utils/format';
import { formatPhone } from '@/utils/phone';

/**
 * One recruiter: the seat, the workspace around it, and what they have done.
 *
 * The one action here is on the *account*, not the seat -- a seat cannot be
 * suspended and an account can. Who holds which workspace role is the
 * company's own decision, made in their team screen; an admin reads it here
 * rather than overriding it, which is why there is no role control.
 */
export function AdminRecruiterDetailPage() {
  const { id } = useParams();

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin-recruiter', id],
    queryFn: () => adminApi.getRecruiter(id),
  });

  if (isPending) return <LoadingState label="Loading the recruiter…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const decisions = Object.entries(data.decisions_by_status ?? {}).filter(([, count]) => count > 0);

  return (
    <div>
      <RecordHeader
        backTo={paths.admin.recruiters}
        backLabel="Recruiters"
        eyebrow="Recruiter"
        title={data.full_name}
        subtitle={[data.job_title, data.company_name].filter(Boolean).join(' · ')}
        badges={
          <>
            <Badge tone="neutral">{roleLabel(data.role)}</Badge>
            <Badge tone={MEMBER_TONES[data.member_status] ?? 'neutral'}>
              {data.member_status === 'INVITED'
                ? 'Invited, not joined'
                : `${humanizeStatus(data.member_status)} seat`}
            </Badge>
            {/* Two badges could both read "Active" -- one is the seat and one
                is the login, and an admin acts on them differently. */}
            <Badge tone={ACCOUNT_TONES[data.account_status] ?? 'neutral'}>
              {humanizeStatus(data.account_status)} account
            </Badge>
            {data.is_owner && <Badge tone="brand">Owns the workspace</Badge>}
            {!data.is_email_verified && <Badge tone="warning">Email unverified</Badge>}
          </>
        }
        actions={
          <>
            <LinkButton
              to={paths.admin.companyDetail(data.company_id)}
              variant="secondary"
              size="sm"
            >
              Open company
            </LinkButton>
            {/* The sanction lands on the account behind the seat: a seat
                cannot be suspended and an account can. */}
            <AccountActions
              account={{
                id: data.user_id,
                full_name: data.full_name,
                status: data.account_status,
                role: 'COMPANY',
              }}
            />
            <VerifyEmailAction
              account={{
                id: data.user_id,
                full_name: data.full_name,
                status: data.account_status,
                is_email_verified: data.is_email_verified,
                role: 'COMPANY',
              }}
            />
            <SignInAsAction
              account={{
                id: data.user_id,
                full_name: data.full_name,
                status: data.account_status,
                role: 'COMPANY',
              }}
            />
          </>
        }
        facts={[
          {
            label: 'Decisions',
            value: data.decisions_count,
            hint: 'Application moves they made',
          },
          { label: 'Interviews', value: data.interviews_count, hint: 'Scheduled by them' },
          { label: 'Offers', value: data.offers_count, hint: 'Issued by them' },
          {
            label: 'Last seen',
            value: data.last_login_at ? formatDate(data.last_login_at) : 'Never',
            hint: data.joined_at
              ? `Joined ${formatDate(data.joined_at)}`
              : `Invited ${formatDate(data.invited_at)}`,
          },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader
              title="The seat"
              description="What this person can do comes from the workspace role, not the account role."
            />
            <FactList>
              <Fact label="Workspace role" value={roleLabel(data.role)} />
              <Fact label="Job title" value={data.job_title} />
              <Fact label="Email" value={data.email} />
              <Fact label="Phone" value={formatPhone(data.phone)} />
              <Fact label="Seat status" value={humanizeStatus(data.member_status)} showEmpty />
              <Fact
                label="Joined"
                value={data.joined_at ? formatDate(data.joined_at) : 'Invitation not taken up'}
              />
              <Fact label="Invited" value={formatDate(data.invited_at)} />
              <Fact label="Invited by" value={data.invited_by_name} />
              <Fact label="Account" value={humanizeStatus(data.account_status)} showEmpty />
              <Fact
                label="Last signed in"
                value={data.last_login_at ? relativeTime(data.last_login_at) : 'Never'}
              />
            </FactList>
          </Card>

          <Card>
            <CardHeader
              title="Decisions"
              description="Every move this person made on a candidate, most recent first."
            />

            {decisions.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {decisions.map(([status, count]) => (
                  <Badge key={status} tone={APPLICATION_TONES[status] ?? 'neutral'}>
                    {APPLICATION_LABELS[status] ?? humanizeStatus(status)} · {count}
                  </Badge>
                ))}
              </div>
            )}

            {data.recent_decisions.length === 0 ? (
              <EmptyState
                compact
                title="Nothing decided yet"
                description={
                  data.member_status === 'INVITED'
                    ? 'The invitation has not been taken up, so there is nothing to show.'
                    : 'This person has not moved an application since they joined.'
                }
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm">
                  <thead className="text-ink-muted text-left">
                    <tr className="border-line border-b">
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Candidate
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Listing
                      </th>
                      <th scope="col" className="py-2 pr-4 font-medium">
                        Moved to
                      </th>
                      <th scope="col" className="py-2 pl-4 text-right font-medium">
                        When
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_decisions.map((decision) => (
                      <tr
                        key={decision.id}
                        className="border-line border-b align-top last:border-0"
                      >
                        <td className="text-ink py-2.5 pr-4">{decision.student_name ?? '—'}</td>
                        <td className="text-ink-muted py-2.5 pr-4">
                          {decision.internship_id ? (
                            <Link
                              to={paths.admin.internshipDetail(decision.internship_id)}
                              className="hover:text-ink hover:underline"
                            >
                              {decision.internship_title}
                            </Link>
                          ) : (
                            (decision.internship_title ?? '—')
                          )}
                        </td>
                        <td className="py-2.5 pr-4">
                          <Badge tone={APPLICATION_TONES[decision.to_status] ?? 'neutral'}>
                            {APPLICATION_LABELS[decision.to_status] ??
                              humanizeStatus(decision.to_status)}
                          </Badge>
                          {decision.from_status && (
                            <span className="text-ink-muted block text-xs">
                              from{' '}
                              {APPLICATION_LABELS[decision.from_status] ??
                                humanizeStatus(decision.from_status)}
                            </span>
                          )}
                        </td>
                        <td className="text-ink-muted py-2.5 pl-4 text-right whitespace-nowrap">
                          {formatDate(decision.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Company" />
            <p className="text-ink text-sm font-medium">
              <Link to={paths.admin.companyDetail(data.company_id)} className="hover:underline">
                {data.company_name}
              </Link>
            </p>
            <p className="text-ink-muted text-xs">{data.company_slug}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge tone={VERIFICATION_TONES[data.company_verification_status] ?? 'neutral'}>
                {humanizeStatus(data.company_verification_status)}
              </Badge>
            </div>
            {data.company_owner_name && (
              <p className="text-ink-muted mt-3 text-xs">Owned by {data.company_owner_name}</p>
            )}
          </Card>

          <Card>
            {/* One recruiter among six is a different thing from the only
                one, which is why the roster travels with the record. */}
            <CardHeader title="The rest of the workspace" />
            {data.teammates.length === 0 ? (
              <EmptyState compact title="Nobody else holds a seat here" />
            ) : (
              <ol className="space-y-3">
                {data.teammates.map((mate) => (
                  <li key={mate.id} className="border-line border-b pb-3 last:border-0 last:pb-0">
                    <p className="text-ink text-sm font-medium">
                      <Link to={paths.admin.recruiterDetail(mate.id)} className="hover:underline">
                        {mate.full_name}
                      </Link>
                      {mate.is_owner && (
                        <Badge tone="brand" className="ml-2">
                          Owner
                        </Badge>
                      )}
                    </p>
                    <p className="text-ink-muted text-xs break-all">{mate.email}</p>
                    <p className="text-ink-muted mt-1 text-xs">
                      {roleLabel(mate.role)}
                      {mate.job_title ? ` · ${mate.job_title}` : ''}
                      {mate.status !== 'ACTIVE' ? ` · ${mate.status.toLowerCase()}` : ''}
                    </p>
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
